import { apiClient, toJsonFile } from "../../services/api";
import type { AvatarPick } from "./stores/avatarPickerStore";

/**
 * `untouched` is not a failure and not a save: the caller opened the picker,
 * changed nothing, and pressed the button. Nothing is written and nothing is
 * reported.
 */
export type AvatarSaveResult = "saved" | "untouched" | "failed";

/**
 * Write whatever the avatar picker holds. Shared by the mobile sheet's avatar
 * view, the desktop settings dialog and the certification panel, so that
 * "remove my photo" cannot come to mean two different requests.
 *
 * The exhaustiveness check sits OUTSIDE the try/catch on purpose: an unhandled
 * pick status is a programming error and must crash, not be reported to the
 * reader as a failed upload.
 */
export async function saveAvatarPick(
  pick: AvatarPick
): Promise<AvatarSaveResult> {
  switch (pick.status) {
    case "untouched":
      return "untouched";
    case "removed":
      try {
        await apiClient.delete("/webapp/private/users/me/avatar");
        return "saved";
      } catch {
        return "failed";
      }
    case "picked":
      try {
        // The picker hands over a data URL, and the endpoint takes a file.
        const response = await fetch(pick.dataUrl);
        const blob = await response.blob();
        const file = new File([blob], "avatar", { type: "image/jpeg" });
        await apiClient.post("/webapp/private/users/me/avatar", {
          json: { file: await toJsonFile(file), pictureName: null },
        });
        return "saved";
      } catch {
        return "failed";
      }
    default:
      throw new Error(pick satisfies never);
  }
}
